import React from 'react';
import { Ruler, Thermometer, Minus, Gauge } from 'lucide-react';

/**
 * Компонент основных параметров трубы
 */
const PipeParametersSection = ({ formData, handleInputChange, errors, pipeTypes }) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
        Основные параметры трубы
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип трубы */}
        <div>
          <label htmlFor="pipeType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Minus className="w-4 h-4 mr-2 text-blue-500" />
            Материал трубы
          </label>
          <select
            id="pipeType"
            name="pipeType"
            value={formData.pipeType}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {pipeTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.pipeType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.pipeType}
            </p>
          )}
        </div>

        {/* Внутренний диаметр */}
        <div>
          <label htmlFor="pipeInnerDiameter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Ruler className="w-4 h-4 mr-2 text-blue-500" />
            Внутренний диаметр (мм)
          </label>
          <input
            type="text"
            id="pipeInnerDiameter"
            name="pipeInnerDiameter"
            value={formData.pipeInnerDiameter}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.pipeInnerDiameter ? 'input-error' : ''}`}
            placeholder="Введите диаметр"
            disabled={formData.calculationType === 'diameter'}
          />
          {errors.pipeInnerDiameter && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.pipeInnerDiameter}
            </p>
          )}
        </div>

        {/* Длина трубы */}
        <div>
          <label htmlFor="pipeLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Minus className="w-4 h-4 mr-2 text-blue-500" />
            Длина трубы (м)
          </label>
          <input
            type="text"
            id="pipeLength"
            name="pipeLength"
            value={formData.pipeLength}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.pipeLength ? 'input-error' : ''}`}
            placeholder="Введите длину"
          />
          {errors.pipeLength && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.pipeLength}
            </p>
          )}
        </div>

        {/* Шероховатость */}
        <div>
          <label htmlFor="pipeRoughness" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Шероховатость (мм)
          </label>
          <input
            type="text"
            id="pipeRoughness"
            name="pipeRoughness"
            value={formData.pipeRoughness}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.pipeRoughness ? 'input-error' : ''}`}
            placeholder="Введите шероховатость"
          />
          {errors.pipeRoughness && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.pipeRoughness}
            </p>
          )}
        </div>

        {/* Давление системы */}
        <div>
          <label htmlFor="systemPressure" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Gauge className="w-4 h-4 mr-2 text-blue-500" />
            Давление в системе (бар)
          </label>
          <input
            type="text"
            id="systemPressure"
            name="systemPressure"
            value={formData.systemPressure}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.systemPressure ? 'input-error' : ''}`}
            placeholder="Введите давление"
          />
          {errors.systemPressure && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.systemPressure}
            </p>
          )}
        </div>

        {/* Температура воды */}
        <div>
          <label htmlFor="waterTemperature" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Thermometer className="w-4 h-4 mr-2 text-blue-500" />
            Температура воды (°C)
          </label>
          <input
            type="text"
            id="waterTemperature"
            name="waterTemperature"
            value={formData.waterTemperature}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.waterTemperature ? 'input-error' : ''}`}
            placeholder="Введите температуру"
          />
          {errors.waterTemperature && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.waterTemperature}
            </p>
          )}
        </div>
      </div>
      
      {/* Таблица подсказок о шероховатости */}
      <div className="mt-4 bg-gray-50 dark:bg-gray-800/40 p-3 rounded-lg text-xs text-gray-600 dark:text-gray-400">
        <div className="font-medium mb-1 text-sm text-gray-800 dark:text-gray-200">Справочные значения шероховатости:</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-2 py-1.5 text-left">Материал трубы</th>
                <th className="px-2 py-1.5 text-left">Шероховатость (мм)</th>
                <th className="px-2 py-1.5 text-left">Комментарий</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Стальная новая</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">0.03-0.15</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Зависит от способа изготовления</td>
              </tr>
              <tr>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Стальная старая</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">0.3-3.0</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Зависит от степени коррозии и отложений</td>
              </tr>
              <tr>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Медная, латунная</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">0.0015-0.03</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Высокая гладкость поверхности</td>
              </tr>
              <tr>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">ПВХ, полипропилен</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">0.004-0.01</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Очень гладкая поверхность</td>
              </tr>
              <tr>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Чугунная</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">0.25-1.0</td>
                <td className="px-2 py-1.5 border-t border-gray-200 dark:border-gray-700">Зависит от способа литья</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PipeParametersSection;